import { useEffect, useRef, useState } from "react";

import {
  getMyReviewState,
  type MyReviewState,
} from "../lib/marketplace-connection-api";

export interface UseMyReviewForProResult {
  /**
   * The server's review state for this pair (`review` + `nextAsk`).
   * `undefined` while in flight / no `proUserId` yet, so no surface ever
   * speculatively offers an ask. A failed read degrades to `undefined` for
   * that `proUserId` — never a fabricated "no review", which would invite a
   * repeat submission the backend refuses with 409.
   */
  state: MyReviewState | undefined;
  /** Re-fetches after a submit, so the surface moves from the ask to the
   *  read-only review without waiting on an unrelated re-render. */
  refetch: () => Promise<void>;
}

/**
 * Plan 435 M4 (mfh-review-prompt). Fetches the buyer's own review state for
 * one pro, lazily: only once the caller has a connected pro to ask about.
 */
export function useMyReviewForPro(
  getToken: () => Promise<string | null>,
  proUserId: string | null | undefined,
): UseMyReviewForProResult {
  const [state, setState] = useState<MyReviewState | undefined>(undefined);
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });

  useEffect(() => {
    if (!proUserId) {
      void Promise.resolve().then(() => setState(undefined));
      return;
    }
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (!cancelled) setState(undefined);
    });
    void getMyReviewState(getTokenRef.current, proUserId)
      .then((result) => {
        if (!cancelled) setState(result);
      })
      .catch(() => {
        if (!cancelled) setState(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [proUserId]);

  const refetch = async () => {
    if (!proUserId) return;
    try {
      setState(await getMyReviewState(getTokenRef.current, proUserId));
    } catch {
      setState(undefined);
    }
  };

  return { state, refetch };
}
